import React from 'react';
import { Box, Typography, Stack, Select, MenuItem, FormControl } from '@mui/material';
import { styled } from '@mui/material/styles';
import { PieChart } from '@mui/x-charts/PieChart';
import TaskCompletionWidget from './TaskCompletionWidget';

const ChartsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: '16px',
  marginBottom: '16px',
  [theme.breakpoints.up('sm')]: {
    gap: '20px',
    marginBottom: '20px'
  },
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '24px',
    marginBottom: '26px'
  }
}));

const ChartCard = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: '100%',
  background: '#ffffff',
  borderRadius: '12px',
  padding: '16px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04)',
  border: '1px solid #e5e7eb',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
    borderRadius: '14px'
  },
  [theme.breakpoints.up('md')]: {
    minWidth: '320px',
    padding: '24px',
    borderRadius: '16px'
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '12px',
  marginBottom: '16px',
  paddingBottom: '12px',
  borderBottom: '1px solid #f1f5f9',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    paddingBottom: '16px'
  }
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  [theme.breakpoints.up('sm')]: {
    gap: '12px'
  }
}));

const IconWrapper = styled(Box)(({ theme, bgcolor }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  backgroundColor: bgcolor || '#f0f9ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '16px',
  [theme.breakpoints.up('sm')]: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    fontSize: '18px'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#111827',
  [theme.breakpoints.up('sm')]: {
    fontSize: '16px'
  }
}));

const TotalBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '5px 10px',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#ffffff',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  [theme.breakpoints.up('sm')]: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px'
  }
}));

const StyledKeywordBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '5px 10px',
  borderRadius: '16px',
  backgroundColor: '#f0f9ff',
  border: '1px solid #bae6fd',
  color: '#0284c7',
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 600,
  maxWidth: '150px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.up('sm')]: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    maxWidth: '200px'
  }
}));

const KeywordSelector = styled(FormControl)(({ theme }) => ({
  minWidth: '120px',
  marginTop: '8px',
  '& .MuiSelect-select': {
    padding: '6px 12px',
    fontSize: '12px',
    fontFamily: 'Inter, sans-serif',
    backgroundColor: '#f0f9ff',
    borderRadius: '16px',
    color: '#0284c7',
    fontWeight: 600
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #bae6fd',
    borderRadius: '16px'
  },
  '& .MuiSelect-icon': {
    color: '#0284c7'
  },
  [theme.breakpoints.up('sm')]: {
    minWidth: '150px',
    '& .MuiSelect-select': {
      padding: '8px 14px',
      fontSize: '13px'
    }
  }
}));

const LocalPackContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    gap: '24px',
    alignItems: 'flex-start'
  }
}));

const ChartSection = styled(Box)({
  flexShrink: 0
});

const StatsSection = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
});

const StatRow = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  borderRadius: '8px',
  background: '#fafafa',
  borderLeft: `4px solid ${color}`,
  transition: 'all 0.2s ease',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    background: '#f5f5f5',
    transform: 'scale(0.99)'
  },
  [theme.breakpoints.up('sm')]: {
    padding: '12px 16px',
    borderRadius: '10px'
  },
  [theme.breakpoints.up('md')]: {
    '&:hover': {
      background: '#f5f5f5',
      transform: 'translateX(2px)'
    },
    '&:active': {
      transform: 'translateX(2px)'
    }
  }
}));

const StatLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  [theme.breakpoints.up('sm')]: {
    gap: '12px'
  }
}));

const StatIcon = styled(Box)(({ theme, bgcolor }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '6px',
  backgroundColor: bgcolor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  color: '#ffffff',
  fontWeight: 700,
  [theme.breakpoints.up('sm')]: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    fontSize: '14px'
  }
}));

const StatInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column'
});

const StatLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#6b7280',
  [theme.breakpoints.up('sm')]: {
    fontSize: '13px'
  }
}));

const StatRank = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '10px',
  color: '#9ca3af',
  [theme.breakpoints.up('sm')]: {
    fontSize: '11px'
  }
}));

const StatValue = styled(Box)(({ color }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end'
}));

const StatNumber = styled(Typography)(({ theme, color }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '18px',
  fontWeight: 700,
  color: color,
  lineHeight: 1,
  [theme.breakpoints.up('sm')]: {
    fontSize: '20px'
  }
}));

const StatPercent = styled(Typography)(({ color }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  fontWeight: 500,
  color: color,
  opacity: 0.8
}));

const ChartsSection = React.memo(({ localPackData, taskCompletion, availableKeywords = [], selectedKeyword = '', onKeywordChange }) => {
  const pieData = React.useMemo(() => {
    const totalPositions = localPackData?.totalPositions || 25;
    const greenPoints = localPackData?.currentRank || 0;
    const yellowPoints = localPackData?.yellowGridPoints || 0;
    const redPoints = localPackData?.redGridPoints || 0;
    const grayPoints = localPackData?.grayGridPoints || 0;

    const data = [];

    if (greenPoints > 0) {
      data.push({ id: 0, value: greenPoints, color: '#10b981', label: 'Rank 1-3' });
    }
    if (yellowPoints > 0) {
      data.push({ id: 1, value: yellowPoints, color: '#f59e0b', label: 'Rank 4-7' });
    }
    if (redPoints > 0) {
      data.push({ id: 2, value: redPoints, color: '#ef4444', label: 'Rank 8-19' });
    }
    if (grayPoints > 0) {
      data.push({ id: 3, value: grayPoints, color: '#9ca3af', label: 'Rank 20+' });
    }

    if (data.length === 0) {
      data.push(
        { id: 0, value: 18, color: '#10b981', label: 'Rank 1-3' },
        { id: 1, value: 7, color: '#e5e7eb', label: 'Other' }
      );
    }

    return data;
  }, [localPackData?.totalPositions, localPackData?.currentRank, localPackData?.yellowGridPoints, localPackData?.redGridPoints, localPackData?.grayGridPoints]);

  const gridPoints = React.useMemo(() => ({
    total: localPackData?.totalPositions || 25,
    green: localPackData?.currentRank || 0,
    yellow: localPackData?.yellowGridPoints || 0,
    red: localPackData?.redGridPoints || 0,
    gray: localPackData?.grayGridPoints || 0
  }), [localPackData]);

  const calculatePercent = (value) => {
    const total = gridPoints.total || 1;
    return Math.round((value / total) * 100);
  };

  const statItems = [
    {
      label: 'Top Positions',
      rank: 'Rank 1-3',
      value: gridPoints.green,
      color: '#10b981',
      bgcolor: '#10b981',
      icon: '🏆'
    },
    {
      label: 'Good Positions',
      rank: 'Rank 4-7',
      value: gridPoints.yellow,
      color: '#f59e0b',
      bgcolor: '#f59e0b',
      icon: '📊'
    },
    {
      label: 'Needs Improvement',
      rank: 'Rank 8-19',
      value: gridPoints.red,
      color: '#ef4444',
      bgcolor: '#ef4444',
      icon: '📈'
    },
    {
      label: 'Not Visible',
      rank: 'Rank 20+',
      value: gridPoints.gray,
      color: '#9ca3af',
      bgcolor: '#9ca3af',
      icon: '👁'
    }
  ].filter(item => item.value > 0);

  return (
    <ChartsContainer>
      <ChartCard>
        <SectionHeader>
          <HeaderLeft>
            <IconWrapper bgcolor="#eff6ff">
              📍
            </IconWrapper>
            <Box>
              <SectionTitle>Local Pack Appearances</SectionTitle>
              {availableKeywords.length > 1 ? (
                <KeywordSelector size="small">
                  <Select
                    value={selectedKeyword || localPackData?.keyword || ''}
                    onChange={(e) => onKeywordChange?.(e.target.value)}
                    displayEmpty
                    size="small"
                  >
                    {availableKeywords.map((kw) => (
                      <MenuItem key={kw} value={kw}>
                        🔍 {kw}
                      </MenuItem>
                    ))}
                  </Select>
                </KeywordSelector>
              ) : localPackData?.keyword && (
                <StyledKeywordBadge sx={{ mt: 0.5 }}>
                  🔍 {localPackData.keyword}
                </StyledKeywordBadge>
              )}
            </Box>
          </HeaderLeft>
          <TotalBadge>
            {gridPoints.total} Grid Points
          </TotalBadge>
        </SectionHeader>

        <LocalPackContent>
          <ChartSection>
            <PieChart
              series={[
                {
                  data: pieData,
                  innerRadius: 45,
                  outerRadius: 70,
                  paddingAngle: 2,
                  cornerRadius: 4
                }
              ]}
              width={160}
              height={160}
              legend={{ hidden: true }}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            />
          </ChartSection>

          <StatsSection>
            {statItems.map((item, index) => (
              <StatRow key={index} color={item.color}>
                <StatLeft>
                  <StatIcon bgcolor={item.bgcolor}>
                    {item.icon}
                  </StatIcon>
                  <StatInfo>
                    <StatLabel>{item.label}</StatLabel>
                    <StatRank>{item.rank}</StatRank>
                  </StatInfo>
                </StatLeft>
                <StatValue>
                  <StatNumber color={item.color}>{item.value}</StatNumber>
                  <StatPercent color={item.color}>{calculatePercent(item.value)}%</StatPercent>
                </StatValue>
              </StatRow>
            ))}
            {statItems.length === 0 && (
              <StatRow color="#10b981">
                <StatLeft>
                  <StatIcon bgcolor="#10b981">🏆</StatIcon>
                  <StatInfo>
                    <StatLabel>No data yet</StatLabel>
                    <StatRank>Run heatmap analysis</StatRank>
                  </StatInfo>
                </StatLeft>
                <StatValue>
                  <StatNumber color="#10b981">-</StatNumber>
                </StatValue>
              </StatRow>
            )}
          </StatsSection>
        </LocalPackContent>
      </ChartCard>

      <ChartCard>
        <SectionHeader>
          <HeaderLeft>
            <IconWrapper bgcolor="#ecfdf5">
              ✓
            </IconWrapper>
            <SectionTitle>Task Assign vs Complete</SectionTitle>
          </HeaderLeft>
        </SectionHeader>

        <TaskCompletionWidget
          percentage={taskCompletion.completedPercentage}
          tasks={taskCompletion.tasks}
          isRealData={taskCompletion.isRealData}
          totalTasks={taskCompletion.totalTasks}
          completedTasks={taskCompletion.completedTasks}
        />
      </ChartCard>
    </ChartsContainer>
  );
});

ChartsSection.displayName = 'ChartsSection';

export default ChartsSection;
